import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import IconInfo from "@image/icones/DocumentsEmployers/iconInfo.svg";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { MALADIE, ABSENCE, VACANCE } from "@helpers/_constants";
import InputHoliday from "@entreprise/employeTab/container/employeeFilesNew/container/JobAndSalary/components/inputHoliday";
import { convertToNumber } from "@helpers/_functions";

const TOOLTIP_MESSAGE = `Ce nombre permet de convertir les journées en heures afin de pouvoir ensuite les déduire lors des demandes de vacances`;

const useStylesBootstrap = makeStyles(() => ({
  arrow: { color: "#3F3F51" },
  tooltip: {
    maxWidth: "358px",
    backgroundColor: "#3F3F51",
    fontFamily: "Poppins_Medium",
    fontSize: "12px",
    padding: "20px",
  },
}));

function BootstrapTooltip(props) {
  const classes = useStylesBootstrap();
  return <Tooltip arrow classes={classes} {...props} />;
}

const getHolidayUnit = (holidayName) => (holidayName?.hourFormat === "hours" ? "h" : "j");

const getHolidayTotal = (holidayName, value, hoursPerDay) => {
  const numericValue = convertToNumber(value);
  if (holidayName?.hourFormat !== "hours" || value === "" || value === null || value === undefined) {
    return numericValue;
  }
  return numericValue * hoursPerDay;
};

const getHolidayFormValue = (holidayName, value, hoursPerDay) => {
  const numericValue = convertToNumber(value);
  if (holidayName?.hourFormat !== "hours" || value === "" || value === null || value === undefined) {
    return numericValue;
  }
  return numericValue / hoursPerDay;
};

export const useSectionHolidayColumns = (employee, useTimeBank) => {
  const { t, i18n } = useTranslation("pages.employés.emploi&Salary");

  return useMemo(() => {
    const columns = [
      { id: "hoursPerDay", label: t("Heures par jours de travail plein") }
    ];

    if (employee?.holidays?.[MALADIE] && !employee.holidays[MALADIE].desactivated) {
      columns.push({ id: MALADIE, label: employee.holidays[MALADIE].designation });
    }

    if (employee?.holidays?.[VACANCE] && !employee.holidays[VACANCE].desactivated) {
      columns.push({ id: VACANCE, label: employee.holidays[VACANCE].designation });
    }

    if (employee?.holidays?.[ABSENCE] && !employee.holidays[ABSENCE].desactivated) {
      columns.push({ id: ABSENCE, label: employee.holidays[ABSENCE].designation });
    }

    employee?.holidays?.otherHolidays?.forEach((holiday) => {
      if (holiday.desactivated) return;
      columns.push({
        id: `other-${holiday?._id}`,
        label: i18n.language === "fr" ? holiday.designationFr : holiday.designationEn
      });
    });

    if (useTimeBank) {
      columns.push({ id: "timebank", label: t("Banque d'heures") });
    }

    return columns;
  }, [employee, useTimeBank, t, i18n.language]);
};

export const SectionHolidayTableBulkHeader = ({ employees, useTimeBank, hiddenColumns = {} }) => {
  const { t: mainTranslator, i18n } = useTranslation(
    "pages.employés.emploi&Salary",
  );
  const firstEmployee = employees[0] || {};

  return (
    <>
      <th
        key="hoursPerDay"
        className={`arh-w-[250px] arh-bg-greyColor arh-px-[1.125rem] arh-py-[10px] arh-text-xs arh-font-semibold arh-text-darkColor arh-whitespace-nowrap ${hiddenColumns.hoursPerDay ? "arh-hidden" : ""}`}
      >
        {mainTranslator("Heures par jours de travail plein")}
        <BootstrapTooltip
          title={mainTranslator(TOOLTIP_MESSAGE)}
          placement="top-end"
        >
          <img
            src={IconInfo}
            alt=""
            className="arh-ml-[1.063rem] arh-cursor-pointer"
          />
        </BootstrapTooltip>
      </th>

      {firstEmployee.holidays?.[MALADIE] &&
        !firstEmployee.holidays[MALADIE].desactivated && (
          <th
            key="sick"
            className={`arh-w-[250px] arh-bg-greyColor arh-px-[1rem] arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[MALADIE] ? "arh-hidden" : ""}`}
          >
            {firstEmployee.holidays[MALADIE].designation}
            {Boolean(firstEmployee.holidays[MALADIE]?.information) && (
              <BootstrapTooltip
                title={firstEmployee.holidays[MALADIE].information}
                placement="top-end"
              >
                <img
                  src={IconInfo}
                  alt=""
                  className="arh-ml-[1.063rem] arh-cursor-pointer"
                />
              </BootstrapTooltip>
            )}
          </th>
        )}

      {firstEmployee.holidays?.[VACANCE] &&
        !firstEmployee.holidays[VACANCE].desactivated && (
          <th
            key="vacation"
            className={`arh-w-[250px] arh-bg-greyColor arh-px-[1.125rem] arh-py-[10px] arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[VACANCE] ? "arh-hidden" : ""}`}
          >
            {firstEmployee.holidays[VACANCE].designation}
            {Boolean(firstEmployee.holidays[VACANCE]?.information) && (
              <BootstrapTooltip
                title={firstEmployee.holidays[VACANCE].information}
                placement="top-end"
              >
                <img
                  src={IconInfo}
                  alt=""
                  className="arh-ml-[1.063rem] arh-cursor-pointer"
                />
              </BootstrapTooltip>
            )}
          </th>
        )}

      {firstEmployee.holidays?.[ABSENCE] &&
        !firstEmployee.holidays[ABSENCE].desactivated && (
          <th
            key="absence"
            className={`arh-w-[250px] arh-bg-greyColor arh-px-[1rem] arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[ABSENCE] ? "arh-hidden" : ""}`}
          >
            {firstEmployee.holidays[ABSENCE].designation}
            {Boolean(firstEmployee.holidays[ABSENCE]?.information) && (
              <BootstrapTooltip
                title={firstEmployee.holidays[ABSENCE].information}
                placement="top-end"
              >
                <img
                  src={IconInfo}
                  alt=""
                  className="arh-ml-[1.063rem] arh-cursor-pointer"
                />
              </BootstrapTooltip>
            )}
          </th>
        )}

      {firstEmployee.holidays?.otherHolidays?.map((holiday) => {
        if (holiday.desactivated) return null;
        return (
          <th
            key={`other-${holiday?._id}`}
            className={`arh-w-[250px] arh-bg-greyColor arh-px-[1.125rem] arh-py-[10px] arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns[`other-${holiday?._id}`] ? "arh-hidden" : ""}`}
          >
            {i18n.language === "fr"
              ? holiday.designationFr
              : holiday.designationEn}
            {Boolean(
              i18n.language === "fr"
                ? holiday?.informationFr
                : holiday?.informationEn,
            ) && (
              <BootstrapTooltip
                title={
                  i18n.language === "fr"
                    ? holiday.informationFr
                    : holiday.informationEn
                }
                placement="top-end"
              >
                <img
                  src={IconInfo}
                  alt=""
                  className="arh-ml-[1.063rem] arh-cursor-pointer"
                />
              </BootstrapTooltip>
            )}
          </th>
        );
      })}

      {useTimeBank && (
        <th
          key="timebank"
          className={`arh-w-[250px] arh-bg-greyColor arh-px-[1.125rem] arh-py-[10px] arh-text-xs arh-font-semibold arh-text-darkColor ${hiddenColumns.timebank ? "arh-hidden" : ""}`}
        >
          {mainTranslator("Banque d'heures")}
        </th>
      )}
    </>
  );
};

export const SectionHolidayTableBulkCells = ({
  employee,
  employeeIndex,
  useTimeBank,
  mustWait,
  readOnly,
  control,
  getValues,
  hiddenColumns = {},
}) => {
  const { t: mainTranslator } = useTranslation("pages.employés.emploi&Salary");

  const getEmployeeHoursPerDay = () => convertToNumber(employee.hoursPerDay, 8);
  const getDisplayHolidayTotal = (holidayName, value) =>
    getHolidayTotal(holidayName, value, getEmployeeHoursPerDay());
  const getFormHolidayValue = (holidayName, value) =>
    getHolidayFormValue(holidayName, value, getEmployeeHoursPerDay());

  const makeRemainingRule = useCallback(
    (name) => {
      return {
        validate: {
          required: (value) => {
            const isSelected = getValues(`employees.${employeeIndex}.selected`);
            if (!isSelected) return true;

            const holidayData = employee.holidays?.[name];
            if (
              Boolean(holidayData?.unlimitedBalance) ||
              !Number.parseFloat(
                getValues(`employees.${employeeIndex}.holiday.${name}`),
              )
            ) {
              return true;
            }

            if (Boolean(holidayData?.notAllowNegativeValue)) {
              return value === "" || Number.parseFloat(value) >= 0;
            }

            return value === "" || !isNaN(Number.parseFloat(value));
          },
        },
      };
    },
    [getValues, employee.holidays, employeeIndex],
  );

  const remainingHolidayRule = useMemo(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;

          const holidayData = employee.holidays?.[VACANCE];
          if (
            Boolean(holidayData?.unlimitedBalance) ||
            !Number.parseFloat(
              getValues(`employees.${employeeIndex}.holiday.hoursHoliday`),
            )
          ) {
            return true;
          }

          if (Boolean(holidayData?.notAllowNegativeValue)) {
            return value === "" || Number.parseFloat(value) >= 0;
          }

          return value === "" || !isNaN(Number.parseFloat(value));
        },
      },
    };
  }, [getValues, employee.holidays, employeeIndex]);

  const hoursPerDayRule = useMemo(() => {
    return {
      validate: {
        required: (value) => {
          const isSelected = getValues(`employees.${employeeIndex}.selected`);
          if (!isSelected) return true;

          const holiday = getValues(`employees.${employeeIndex}.holiday`);
          if (
            (employee.holidays?.[MALADIE]?.unlimitedBalance &&
              employee.holidays?.[ABSENCE]?.unlimitedBalance &&
              employee.holidays?.[VACANCE]?.unlimitedBalance) ||
            (!Number.parseFloat(holiday?.hoursHoliday) &&
              !Number.parseFloat(holiday?.hoursMiss) &&
              !Number.parseFloat(holiday?.hoursSick))
          ) {
            return true;
          }
          return !!Number.parseFloat(value);
        },
      },
    };
  }, [getValues, employee.holidays, employeeIndex]);

  return (
    <>
      <td
        key="hoursPerDay"
        className={`arh-w-[250px] arh-bg-greyColor arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.hoursPerDay ? "arh-hidden" : ""}`}
      >
        <Controller
          control={control}
          name={`employees.${employeeIndex}.holiday.hoursPerDay`}
          defaultValue={employee.hoursPerDay || 8}
          rules={hoursPerDayRule}
          render={({ field: { onChange, value }, fieldState: { invalid } }) => (
            <InputHoliday
              label=" "
              total={value}
              clickable={true}
              wait={mustWait}
              disabled={mustWait || readOnly}
              invalid={invalid}
              onChange={
                !readOnly && !mustWait
                  ? (newValue) => {
                      onChange(newValue);
                    }
                  : null
              }
              maxValue={24}
              edit={true}
              unit="h"
              width="!arh-w-[12rem]"
              additionnalClasLablel="arh-whitespace-nowrap"
            />
          )}
        />
      </td>

      {employee.holidays?.[MALADIE] &&
        !employee.holidays[MALADIE].desactivated && (
          <td
            key="sick"
            className={`arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[MALADIE] ? "arh-hidden" : ""}`}
          >
            <div className="arh-flex arh-gap-2">
              <div className="arh-flex-1">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.holidayRemaining.hoursSick`}
                  defaultValue={employee.holidays[MALADIE].hoursRemaining || 0}
                  rules={makeRemainingRule(MALADIE)}
                  render={({
                    field: { onChange, value },
                    fieldState: { invalid },
                  }) => (
                    <InputHoliday
                      label={mainTranslator("Solde actuel")}
                      total={
                        Boolean(employee.holidays[MALADIE]?.unlimitedBalance)
                          ? mainTranslator("Illimité")
                          : getDisplayHolidayTotal(employee.holidays[MALADIE], value)
                      }
                      clickable={true}
                      wait={mustWait}
                      disabled={
                        employee.holidays[MALADIE]?.notVisible ||
                        Boolean(employee.holidays[MALADIE]?.unlimitedBalance) ||
                        mustWait ||
                        readOnly
                      }
                      notVisible={
                        employee.holidays[MALADIE]?.notVisible ||
                        Boolean(employee.holidays[MALADIE]?.unlimitedBalance)
                      }
                      allowNegative={!Boolean(employee.holidays[MALADIE]?.notAllowNegativeValue)}
                      onChange={
                        !Boolean(
                          employee.holidays[MALADIE]?.unlimitedBalance,
                        ) &&
                        !readOnly &&
                        !mustWait
                          ? (newValue) => onChange(getFormHolidayValue(employee.holidays[MALADIE], newValue))
                          : null
                      }
                      edit={true}
                      unit={getHolidayUnit(employee.holidays[MALADIE])}
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
              <div className="arh-flex arh-items-end arh-justify-center arh-text-[#D2D8E2] arh-mb-2">
                /
              </div>
              <div className="arh-flex-1">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.holiday.hoursSick`}
                  defaultValue={employee.holidays[MALADIE].hours || 0}
                  render={({ field: { onChange, value } }) => (
                    <InputHoliday
                      label={mainTranslator("Droit annuel")}
                      total={
                        Boolean(employee.holidays[MALADIE]?.unlimitedBalance)
                          ? mainTranslator("Illimité")
                          : getDisplayHolidayTotal(employee.holidays[MALADIE], value)
                      }
                      clickable={true}
                      wait={mustWait}
                      disabled={
                        employee.holidays[MALADIE]?.notVisible ||
                        Boolean(employee.holidays[MALADIE]?.unlimitedBalance) ||
                        mustWait ||
                        readOnly
                      }
                      notVisible={employee.holidays[MALADIE]?.notVisible}
                      onChange={
                        !Boolean(
                          employee.holidays[MALADIE]?.unlimitedBalance,
                        ) &&
                        !readOnly &&
                        !mustWait
                          ? (newValue) => onChange(getFormHolidayValue(employee.holidays[MALADIE], newValue))
                          : null
                      }
                      edit={true}
                      unit={getHolidayUnit(employee.holidays[MALADIE])}
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
            </div>
          </td>
        )}

      {employee.holidays?.[VACANCE] &&
        !employee.holidays[VACANCE].desactivated && (
          <td
            key="vacation"
            className={`arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[VACANCE] ? "arh-hidden" : ""}`}
          >
            <div className="arh-flex arh-gap-2">
              <div className="arh-flex-1">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.holidayRemaining.hoursHoliday`}
                  defaultValue={employee.holidays[VACANCE].hoursRemaining || 0}
                  rules={remainingHolidayRule}
                  render={({
                    field: { onChange, value },
                    fieldState: { invalid },
                  }) => (
                    <InputHoliday
                      label={mainTranslator("Solde actuel")}
                      total={
                        Boolean(employee.holidays[VACANCE]?.unlimitedBalance)
                          ? mainTranslator("Illimité")
                          : getDisplayHolidayTotal(employee.holidays[VACANCE], value)
                      }
                      clickable={true}
                      wait={mustWait}
                      disabled={
                        employee.holidays[VACANCE]?.notVisible ||
                        Boolean(employee.holidays[VACANCE]?.unlimitedBalance) ||
                        mustWait ||
                        readOnly
                      }
                      notVisible={
                        employee.holidays[VACANCE]?.notVisible ||
                        Boolean(employee.holidays[VACANCE]?.unlimitedBalance)
                      }
                      allowNegative={!Boolean(employee.holidays[VACANCE]?.notAllowNegativeValue)}
                      onChange={
                        !Boolean(
                          employee.holidays[VACANCE]?.unlimitedBalance,
                        ) &&
                        !readOnly &&
                        !mustWait
                          ? (newValue) => onChange(getFormHolidayValue(employee.holidays[VACANCE], newValue))
                          : null
                      }
                      edit={true}
                      unit={getHolidayUnit(employee.holidays[VACANCE])}
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
              <div className="arh-flex arh-items-end arh-justify-center arh-text-[#D2D8E2] arh-mb-2">
                /
              </div>
              <div className="arh-flex-1">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.holiday.hoursHoliday`}
                  defaultValue={employee.holidays[VACANCE].hours || 0}
                  render={({ field: { onChange, value } }) => (
                    <InputHoliday
                      label={mainTranslator("Droit annuel")}
                      total={
                        Boolean(employee.holidays[VACANCE]?.unlimitedBalance)
                          ? mainTranslator("Illimité")
                          : getDisplayHolidayTotal(employee.holidays[VACANCE], value)
                      }
                      clickable={true}
                      wait={mustWait}
                      disabled={
                        employee.holidays[VACANCE]?.notVisible ||
                        Boolean(employee.holidays[VACANCE]?.unlimitedBalance) ||
                        mustWait ||
                        readOnly
                      }
                      notVisible={employee.holidays[VACANCE]?.notVisible}
                      onChange={
                        !Boolean(
                          employee.holidays[VACANCE]?.unlimitedBalance,
                        ) &&
                        !readOnly &&
                        !mustWait
                          ? (newValue) => onChange(getFormHolidayValue(employee.holidays[VACANCE], newValue))
                          : null
                      }
                      edit={true}
                      unit={getHolidayUnit(employee.holidays[VACANCE])}
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
            </div>
          </td>
        )}

      {employee.holidays?.[ABSENCE] &&
        !employee.holidays[ABSENCE].desactivated && (
          <td
            key="absence"
            className={`arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[ABSENCE] ? "arh-hidden" : ""}`}
          >
            <div className="arh-flex arh-gap-2">
              <div className="arh-flex-1">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.holidayRemaining.hoursMiss`}
                  defaultValue={employee.holidays[ABSENCE].hoursRemaining || 0}
                  rules={makeRemainingRule(ABSENCE)}
                  render={({
                    field: { onChange, value },
                    fieldState: { invalid },
                  }) => (
                    <InputHoliday
                      label={mainTranslator("Solde actuel")}
                      total={
                        Boolean(employee.holidays[ABSENCE]?.unlimitedBalance)
                          ? mainTranslator("Illimité")
                          : getDisplayHolidayTotal(employee.holidays[ABSENCE], value)
                      }
                      clickable={true}
                      wait={mustWait}
                      disabled={
                        employee.holidays[ABSENCE]?.notVisible ||
                        Boolean(employee.holidays[ABSENCE]?.unlimitedBalance) ||
                        mustWait ||
                        readOnly
                      }
                      notVisible={
                        employee.holidays[ABSENCE]?.notVisible ||
                        Boolean(employee.holidays[ABSENCE]?.unlimitedBalance)
                      }
                      allowNegative={!Boolean(employee.holidays[ABSENCE]?.notAllowNegativeValue)}
                      onChange={
                        !Boolean(
                          employee.holidays[ABSENCE]?.unlimitedBalance,
                        ) &&
                        !readOnly &&
                        !mustWait
                          ? (newValue) => onChange(getFormHolidayValue(employee.holidays[ABSENCE], newValue))
                          : null
                      }
                      edit={true}
                      unit={getHolidayUnit(employee.holidays[ABSENCE])}
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
              <div className="arh-flex arh-items-end arh-justify-center arh-text-[#D2D8E2] arh-mb-2">
                /
              </div>
              <div className="arh-flex-1">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.holiday.hoursMiss`}
                  defaultValue={employee.holidays[ABSENCE].hours || 0}
                  render={({ field: { onChange, value } }) => (
                    <InputHoliday
                      label={mainTranslator("Droit annuel")}
                      total={
                        Boolean(employee.holidays[ABSENCE]?.unlimitedBalance)
                          ? mainTranslator("Illimité")
                          : getDisplayHolidayTotal(employee.holidays[ABSENCE], value)
                      }
                      clickable={true}
                      wait={mustWait}
                      disabled={
                        employee.holidays[ABSENCE]?.notVisible ||
                        Boolean(employee.holidays[ABSENCE]?.unlimitedBalance) ||
                        mustWait ||
                        readOnly
                      }
                      notVisible={employee.holidays[ABSENCE]?.notVisible}
                      onChange={
                        !Boolean(
                          employee.holidays[ABSENCE]?.unlimitedBalance,
                        ) &&
                        !readOnly &&
                        !mustWait
                          ? (newValue) => onChange(getFormHolidayValue(employee.holidays[ABSENCE], newValue))
                          : null
                      }
                      edit={true}
                      unit={getHolidayUnit(employee.holidays[ABSENCE])}
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
            </div>
          </td>
        )}

      {employee.holidays?.otherHolidays?.map((holiday, holidayIndex) => {
        if (holiday.desactivated) return null;
        if (!holiday.nethris) {
          return (
            <td
              key={`other-${holiday?._id}`}
              className={`arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`other-${holiday?._id}`] ? "arh-hidden" : ""}`}
            >
              <div className="arh-flex arh-gap-2">
                <div className="arh-flex-1">
                  <Controller
                    control={control}
                    name={`employees.${employeeIndex}.otherHolidays.${holidayIndex}.hoursRemaining`}
                    defaultValue={holiday.hoursRemaining || 0}
                    render={({ field: { onChange, value } }) => (
                      <InputHoliday
                        label={mainTranslator("Solde actuel")}
                        total={
                          holiday?.unlimitedBalance
                            ? mainTranslator("Illimité")
                            : getDisplayHolidayTotal(holiday, value)
                        }
                        clickable={true}
                        wait={mustWait}
                        disabled={
                          holiday?.notVisible ||
                          holiday?.unlimitedBalance ||
                          mustWait ||
                          readOnly
                        }
                        notVisible={
                          holiday?.notVisible || holiday?.unlimitedBalance
                        }
                        allowNegative={!Boolean(holiday?.notAllowNegativeValue)}
                        onChange={
                          !holiday?.unlimitedBalance && !readOnly && !mustWait
                            ? (newValue) => onChange(getFormHolidayValue(holiday, newValue))
                            : null
                        }
                        edit={true}
                        unit={getHolidayUnit(holiday)}
                        width="arh-w-full"
                        additionnalClasLablel="arh-whitespace-nowrap"
                      />
                    )}
                  />
                </div>
                <div className="arh-flex arh-items-end arh-justify-center arh-text-[#D2D8E2] arh-mb-2">
                  /
                </div>
                <div className="arh-flex-1">
                  <Controller
                    control={control}
                    name={`employees.${employeeIndex}.otherHolidays.${holidayIndex}.hours`}
                    defaultValue={holiday.hours || 0}
                    render={({ field: { onChange, value } }) => (
                      <InputHoliday
                        label={mainTranslator("Droit annuel")}
                        total={
                          holiday?.unlimitedBalance
                            ? mainTranslator("Illimité")
                            : getDisplayHolidayTotal(holiday, value)
                        }
                        clickable={true}
                        wait={mustWait}
                        disabled={
                          holiday?.notVisible ||
                          holiday?.unlimitedBalance ||
                          mustWait ||
                          readOnly
                        }
                        notVisible={holiday?.notVisible}
                        onChange={
                          !holiday?.unlimitedBalance && !readOnly && !mustWait
                            ? (newValue) => onChange(getFormHolidayValue(holiday, newValue))
                            : null
                        }
                        edit={true}
                        unit={getHolidayUnit(holiday)}
                        width="arh-w-full"
                        additionnalClasLablel="arh-whitespace-nowrap"
                      />
                    )}
                  />
                </div>
              </div>
              <input
                type="hidden"
                {...control.register(
                  `employees.${employeeIndex}.otherHolidays.${holidayIndex}._id`,
                )}
                defaultValue={holiday._id}
              />
            </td>
          );
        } else {
          return (
            <td
              key={`other-${holiday?._id}`}
              className={`arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns[`other-${holiday?._id}`] ? "arh-hidden" : ""}`}
            >
              <div className="arh-w-[180px] arh-flex-1 arh-items-center">
                <Controller
                  control={control}
                  name={`employees.${employeeIndex}.otherHolidays.${holidayIndex}.hourlyRateExport`}
                  defaultValue={holiday.hourlyRateExport}
                  render={({ field: { onChange, value } }) => (
                    <InputHoliday
                      label={mainTranslator("Taux horaire pour l'exportation")}
                      total={value}
                      clickable={true}
                      contentClass="arh-w-[11rem]"
                      wait={mustWait}
                      disabled={ 
                        holiday?.notVisible ||
                          mustWait ||
                          readOnly} 
                      onChange={(newValue) => onChange(newValue)}
                      unit=""
                      edit
                      width="arh-w-full"
                      additionnalClasLablel="arh-whitespace-nowrap"
                    />
                  )}
                />
              </div>
              <div className="arh-flex-1">
                <input
                  {...control.register(`employees.${employeeIndex}.otherHolidays.${holidayIndex}._id`)}
                  type="hidden"
                  defaultValue={holiday._id}
                />
              </div>
            </td>
          )
        }
      })}

      {useTimeBank && (
        <td
          key="timebank"
          className={`arh-w-[250px] arh-p-4 arh-px-5 arh-font-poppins arh-text-xs arh-text-darkGreyColor ${hiddenColumns.timebank ? "arh-hidden" : ""}`}
        >
          <Controller
            control={control}
            name={`employees.${employeeIndex}.timebank`}
            defaultValue={employee.timebank || 0}
            render={({ field: { onChange, value } }) => (
              <InputHoliday
                label={mainTranslator("Solde actuel")}
                total={value}
                clickable={!readOnly}
                disabled={ mustWait || readOnly}
                edit={!readOnly}
                unit="h"
                width="arh-w-full"
                additionnalClasLablel="arh-whitespace-nowrap"
                onChange={onChange}
                allowNegative={true}
              />
            )}
          />
        </td>
      )}
    </>
  );
};

export const getHolidayColumnCount = (employees, useTimeBank) => {
  if (!employees.length) return 0;

  const firstEmployee = employees[0];
  let count = 1;

  if (
    firstEmployee.holidays?.[MALADIE] &&
    !firstEmployee.holidays[MALADIE].desactivated
  ) {
    count++;
  }

  if (
    firstEmployee.holidays?.[VACANCE] &&
    !firstEmployee.holidays[VACANCE].desactivated
  ) {
    count++;
  }

  if (
    firstEmployee.holidays?.[ABSENCE] &&
    !firstEmployee.holidays[ABSENCE].desactivated
  ) {
    count++;
  }

  if (firstEmployee.holidays?.otherHolidays?.length) {
    count += firstEmployee.holidays.otherHolidays.filter(
      (holiday) => !holiday.desactivated,
    ).length;
  }

  if (useTimeBank) {
    count++;
  }

  return count;
};
